import { baseApi } from "~/app/store/baseApi";
import type { SalesPartnerContextData } from "~/domains/affiliate/types/models";
import type { SelectedAccountData, SessionUserData } from "~/domains/user/types/SessionUserData";
import type { PriceReferenceSelection } from "~/domains/priceReference/types";

/**
 * Data contracts for Bootstrap Data and Session Data endpoints.
 *
 * Bootstrap Data (GET /page-data/bootstrap) is site-level configuration identical for every visitor on a
 * given site, locale, and currency. It is cacheable across visitors and contains nothing user-specific.
 *
 * Session Data (GET /page-data/session) is per-visitor state including anonymous visitors (whose
 * user/account fields are null). It is never cacheable across visitors.
 *
 * See ADR 0003 and CONTEXT.md ("Bootstrap Data" / "Session Data").
 */

export interface BatchingPnaData {
  priceEnabled?: boolean;
  availabilityEnabled?: boolean;
  batches?: number[];
}

export interface BatchingLineItemUpdateData {
  enabled?: boolean;
  batches?: number[];
}

export interface BatchingData {
  interval?: number;
  sizeResetTime?: number;
  pna?: BatchingPnaData;
  lineItemUpdate?: BatchingLineItemUpdateData;
}

export interface ConfigurationData {
  viteDev?: boolean;
  baseJsPath?: string;
  jsVariant?: string;
  oneCodeImplPath?: string;
  baseCssPath?: string;
  baseTranslationsPath?: string;
  cdnCacheBuster?: string;
  reactPreferredProductsUrl?: string;
  bulkMaxRows?: string;
  rddMaxYearsAhead?: number;
  houseNumberFirstInAddress?: boolean;
  maxCartSizeWithPnaCall?: string;
  forceLoginPopup?: boolean;
  marketingConsentEnabled?: boolean;
  linearConfiguratorBannerName?: string;
  spfImperial?: boolean;
  addedToCartRelatedProductsDesktop?: number;
  addedToCartRelatedProductsMobile?: number;
  addedToCartTopsellerProductsDesktop?: number;
  addedToCartTopsellerProductsMobile?: number;
  relatedProductWhitelist?: string[];
  salesPartnerStockIntegrationEnabled?: boolean;
  showTopsellerRecoInSearch?: boolean;
  showFccGapProductsRecommendation?: boolean;
  productFruitsIntegrationEnabled?: boolean;
  webComponentEnabledTabs?: string[];
  mountingMgrWebComponentUrl?: string;
  beCalculationEnabled?: boolean;
  greaseAppWebComponentUrl?: string;
  zoovuProjectId?: string;
  affiliateExpirationTimeHours?: number;
  // Site-identity / site-config fields populated by the bootstrap facade (ADR 0003).
  siteUid?: string;
  availableLanguages?: string[];
  ccMinJSFileURL?: string;
  sessionTimeout?: number;
  autoLogoutMessageTime?: number;
  disableAllWS?: boolean;
  batching?: BatchingData;
}

export interface FormattingData {
  dateFormat?: string;
  datetimeFormat?: string;
  timeFormat?: string;
  userFormattingLocale?: string;
  decimalSeparator?: string;
  thousandSeparator?: string;
  // Currency/timezone fields populated by the bootstrap facade (ADR 0003).
  currencyIso?: string;
  currencyDecimalDigits?: number;
  timezoneOffset?: number;
}

export interface ResourceData {
  footerCopyright?: string;
  tourProductId?: string;
  contextPath?: string;
  productPrefix?: string;
  marketingCloudIdentifier?: string;
}

export interface UrlsData {
  lmsTermsConditions?: string;
  contactUs?: string;
  mmdUrl?: string;
}

export interface RegionSelectionData {
  showRedirectBanner?: boolean;
  dropdownUrls?: Record<string, string>;
}

export interface ExperimentPlatformData {
  qualtricsLanguage?: string;
  hotjarEnabled?: boolean;
  hjsvValue?: string;
  hjidValue?: string;
  kameleoonEnabled?: boolean;
  kameleoonProjectCode?: string;
  kameleoonIframeUrl?: string;
}

export interface ExperimentUserData {
  hotjarUserType?: string;
  hotjarUserRole?: string;
  kameleoonUserType?: string;
  kameleoonUserRole?: string;
}

export interface BootstrapData {
  configuration: ConfigurationData;
  formatting: FormattingData;
  resource: ResourceData;
  urls: UrlsData;
  experiment: ExperimentPlatformData;
}

export const DELIVERY_DATE_MEANING = {
  SHIPMENT: "SHIPMENT",
  DELIVERY: "DELIVERY",
} as const;

export type DeliveryDateMeaning = (typeof DELIVERY_DATE_MEANING)[keyof typeof DELIVERY_DATE_MEANING];

export type PunchoutMode = "OCI" | "CXML";

export interface CommerceContextData {
  punchoutMode?: PunchoutMode | null;
  offsetRDD?: number;
  orderFulfilmentHintThresholdPercent?: number;
  lineLevelCustomerOrderNumberEnabled?: boolean;
  packageRestrictionEnabled?: boolean;
  sundayAsDeliveryDayEnabled?: boolean;
  shipDateToWeekEnabled?: boolean;
  phaseOutEnabled?: boolean;
  priceReferenceSelection?: PriceReferenceSelection;
  salesCampaignDaysLimitRDD?: number;
  linearConfiguratorEmail?: string;
  fccShowNewLabelEnabled?: boolean;
  fccShowBannerEnabled?: boolean;
  quotationSplitEnabled?: boolean;
  targetPriceEnabled?: boolean;
  showExclusivePack?: boolean;
  homepagePersonalizationComponentEnabled?: boolean;
  selectedAccount?: SelectedAccountData | null;
  zoovuEnabled?: boolean;
  deliveryDateMeaning?: DeliveryDateMeaning;
  showPriceRefOnCartButton?: boolean;
  newProductVersionEnabled?: boolean;
  multipleAccountSearchEnabled?: boolean;
  showEdit?: boolean;
}

export type UserData = SessionUserData;

export interface SalesRepresentativeData {
  phone?: string;
}

export interface AnalyticsData {
  userHash?: string;
  userRoles?: string;
  userId?: string;
  companyName?: string;
  salesChannel?: string;
  industrialSection?: string;
}

export interface ProductComparisonData {
  maxSize?: number;
  productCodes?: string[];
}

export interface SessionCartIdentityData {
  guid: string;
  code: string;
}

export interface SessionData {
  user: UserData;
  salesRepresentative?: SalesRepresentativeData;
  analytics: AnalyticsData;
  commerceContext: CommerceContextData;
  salesPartnerContext: SalesPartnerContextData;
  productComparison: ProductComparisonData;
  salesCampaign: Record<string, unknown>;
  regionSelection: RegionSelectionData;
  activeCart: SessionCartIdentityData | null;
  mergeSourceCart: SessionCartIdentityData | null;
  experiment: ExperimentUserData;
}

export const bffApi = baseApi.injectEndpoints({
  endpoints: () => ({}),
});

/**
 * Invalidates the Minicart RTK Query tag, triggering a refetch of the explicit Active Cart projection.
 * Replaces the former window.SCHA.updateMinicart() / clearMinicart() fn pointers (ADR 0003).
 *
 * Exported from the app layer so all features (including siblings like QuoteSplit) can import it
 * without violating the sibling-feature isolation rule.
 */
export const invalidateMinicart = () => {
  void import("~/app/store/getStore").then(({ getStore }) => {
    getStore().dispatch(bffApi.util.invalidateTags(["Minicart"]));
  });
};
